import type { V10ControlRoomSection } from "./v10-phase-model.js";
import { V10_CONTROL_ROOM_SECTIONS } from "./v10-phase-model.js";

export type V10ProjectRoomDomainRecordId =
  | "studio.intakeSummary"
  | "studio.businessGraphRevision"
  | "studio.generationRun"
  | "studio.clientReview"
  | "studio.clientApproval"
  | "studio.changeRequest"
  | "studio.launchProof"
  | "studio.searchReadinessReport"
  | "studio.departureKit"
  | "studio.liveCareNote"
  | "studio.projectNotes"
  | "studio.projectActivity"
  | "studio.projectAdvanced";

export type V10ProjectRoomDomainRecord = {
  readonly id: V10ProjectRoomDomainRecordId;
  readonly label: string;
  readonly section: V10ControlRoomSection;
  readonly truthSource: string;
  readonly panelTestId: string;
};

/** Single atlas of domain records reachable from Project Room without duplicating truth. */
export const V10_PROJECT_ROOM_DOMAIN_ATLAS: readonly V10ProjectRoomDomainRecord[] = [
  {
    id: "studio.intakeSummary",
    label: "Client intake",
    section: "overview",
    truthSource: "api.getIntakeSummary / lifecycle.intakeSummary",
    panelTestId: "v10-domain-intake",
  },
  {
    id: "studio.businessGraphRevision",
    label: "Business Profile revision",
    section: "business",
    truthSource: "api.businessGraph / project.currentBusinessGraphRevisionId",
    panelTestId: "v10-domain-business-graph",
  },
  {
    id: "studio.generationRun",
    label: "Generation run",
    section: "website",
    truthSource: "api.listRuns / lifecycle.latestRun",
    panelTestId: "v10-domain-generation-run",
  },
  {
    id: "studio.clientReview",
    label: "Client review & preview",
    section: "website",
    truthSource: "api.getClientReviewStatus",
    panelTestId: "v10-domain-client-review",
  },
  {
    id: "studio.clientApproval",
    label: "Client approval decision",
    section: "approval-delivery",
    truthSource: "clientReview.latestApproval",
    panelTestId: "v10-domain-client-approval",
  },
  {
    id: "studio.changeRequest",
    label: "Change request",
    section: "changes",
    truthSource: "clientReview.latestChangeRequest",
    panelTestId: "v10-domain-change-request",
  },
  {
    id: "studio.launchProof",
    label: "Launch proof",
    section: "business",
    truthSource: "LaunchProofPanel / api launch proof endpoints",
    panelTestId: "v10-domain-launch-proof",
  },
  {
    id: "studio.searchReadinessReport",
    label: "Search readiness report",
    section: "business",
    truthSource: "ReportsPanel / api reports",
    panelTestId: "v10-domain-readiness-report",
  },
  {
    id: "studio.departureKit",
    label: "Departure Kit / handoff",
    section: "approval-delivery",
    truthSource: "api.getDepartureKitStatus / downloadDepartureKit",
    panelTestId: "v10-domain-departure-kit",
  },
  {
    id: "studio.liveCareNote",
    label: "Post-handoff care",
    section: "overview",
    truthSource: "lifecycle handoff_ready phase + overview live-care panels",
    panelTestId: "v10-domain-live-care",
  },
  {
    id: "studio.projectNotes",
    label: "Project notes",
    section: "notes",
    truthSource: "step5 notes API / project notes surface",
    panelTestId: "v10-domain-notes",
  },
  {
    id: "studio.projectActivity",
    label: "Project activity",
    section: "activity",
    truthSource: "step5 activity / audit trail surface",
    panelTestId: "v10-domain-activity",
  },
  {
    id: "studio.projectAdvanced",
    label: "Advanced project settings",
    section: "advanced",
    truthSource: "step5 project settings / advanced surface",
    panelTestId: "v10-domain-advanced",
  },
] as const;

export function domainRecordsForSection(section: V10ControlRoomSection): readonly V10ProjectRoomDomainRecord[] {
  return V10_PROJECT_ROOM_DOMAIN_ATLAS.filter((row) => row.section === section);
}

export function assertProjectRoomDomainAtlasCoversAllSections(): {
  readonly ok: boolean;
  readonly missingSections: readonly V10ControlRoomSection[];
} {
  const covered = new Set(V10_PROJECT_ROOM_DOMAIN_ATLAS.map((row) => row.section));
  const missingSections = V10_CONTROL_ROOM_SECTIONS.filter((section) => !covered.has(section));
  return { ok: missingSections.length === 0, missingSections };
}
